import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import API from '../api';
import Modal from './Modal';
import { slugify, sanitizeSlugInput } from '../utils/slug';

const EMPTY = { category_id: '', brand_id: '', name: '', slug: '', description: '', image_url: '', images: [], specs: '', sort_order: 0, is_active: 1 };
const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:5000';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');

  const load = () =>
    Promise.all([API.get('/products/all'), API.get('/categories/all'), API.get('/brands/all')])
      .then(([p, c, b]) => { setProducts(p.data); setCategories(c.data); setBrands(b.data); })
      .catch(() => {});

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    const nextOrder = products.length ? Math.max(...products.map(i => i.sort_order)) + 1 : 0;
    setForm({ ...EMPTY, sort_order: nextOrder });
    setModal({ mode: 'add' });
  };
  const openEdit = (item) => { setForm({ ...item, category_id: item.category_id || '', brand_id: item.brand_id || '', images: item.images || [] }); setModal({ mode: 'edit', id: item.id }); };

  const handleNameChange = (name) => {
    setForm(f => ({ ...f, name, slug: modal?.mode === 'add' ? slugify(name) : f.slug }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, image_url: data.url }));
    } catch (err) { alert(err.response?.data?.error || 'Upload failed'); }
    setUploading(false);
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData(); fd.append('file', file);
        const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded.push(data.url);
      }
      setForm(f => ({ ...f, images: [...(f.images || []), ...uploaded] }));
    } catch (err) {
      alert(err.response?.data?.error || 'One or more images failed to upload');
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeGalleryImage = (idx) => {
    setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, category_id: form.category_id || null, brand_id: form.brand_id || null };
      if (modal.mode === 'add') await API.post('/products', payload);
      else await API.put(`/products/${modal.id}`, payload);
      setModal(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await API.delete(`/products/${id}`); load();
  };

  const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;

  const filtered = products.filter(p => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.brand_name?.toLowerCase().includes(search.toLowerCase());
    const matchCat = !filterCat || String(p.category_id) === filterCat;
    return matchSearch && matchCat;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-primary-dark">Products</h1>
          <p className="text-sm text-gray-500 mt-1">{products.length} products total</p>
        </div>
        <button onClick={openAdd} className="btn-primary text-sm flex items-center gap-2">
          <PlusIcon className="w-4 h-4" /> Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-100 shadow-sm p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 focus:outline-none focus:border-primary" />
        </div>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          className="border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {['Image', 'Name', 'Category', 'Brand', 'Status', 'Views', ''].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No products found.</td></tr>
            )}
            {filtered.map(item => (
              <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-3">
                  <div className="w-12 h-9 bg-gray-100 overflow-hidden">
                    {item.image_url
                      ? <img src={imgSrc(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-primary/10" />}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800 max-w-xs truncate">{item.name}</div>
                  {item.description && <div className="text-xs text-gray-400 truncate max-w-xs">{item.description}</div>}
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">{item.category_name || '—'}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{item.brand_name || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 ${item.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {item.is_active ? 'Active' : 'Hidden'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500">{item.unique_view_count ?? 0} <span className="text-gray-400 text-xs">({item.view_count ?? 0})</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(item)} className="p-1.5 text-gray-400 hover:text-primary border border-gray-200 hover:border-primary transition-colors">
                      <PencilIcon className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-1.5 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors">
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.mode === 'add' ? 'Add Product' : 'Edit Product'} onClose={() => setModal(null)} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Product Name *</label>
                <input value={form.name} onChange={e => handleNameChange(e.target.value)}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Slug *</label>
                <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: sanitizeSlugInput(e.target.value) }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Category</label>
                <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  <option value="">— No Category —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Brand</label>
                <select value={form.brand_id} onChange={e => setForm(f => ({ ...f, brand_id: e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  <option value="">— No Brand —</option>
                  {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">Controls where this product appears when browsing by brand. Manage brands under the Brands page.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
                <select value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: +e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  <option value={1}>Active</option>
                  <option value={0}>Hidden</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Description</label>
              <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={3} className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary resize-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Specifications</label>
              <textarea value={form.specs || ''} onChange={e => setForm(f => ({ ...f, specs: e.target.value }))}
                rows={5} placeholder={'Plain paragraphs, or:\n## Section Heading\n- Bullet point\n- Another bullet point'}
                className="w-full border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary resize-none" />
              <p className="text-xs text-gray-400 mt-1">Start a line with <code className="font-mono">##</code> for a heading, <code className="font-mono">-</code> for a bullet list. Blank lines start a new paragraph. Links are made clickable automatically.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Cover Image</label>
              {form.image_url && <img src={form.image_url?.startsWith('/') ? `${BASE}${form.image_url}` : form.image_url} alt="preview" className="h-24 w-full object-cover border border-gray-200 mb-2" />}
              <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading}
                className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:border-0 file:bg-primary file:text-white file:text-xs file:cursor-pointer mb-2" />
              <input value={form.image_url || ''} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                placeholder="or paste image URL..." className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              <p className="text-xs text-gray-400 mt-1">Shown on product cards and as the main gallery image.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Additional Gallery Images</label>
              {form.images?.length > 0 && (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-2">
                  {form.images.map((url, i) => (
                    <div key={i} className="relative group h-16 border border-gray-200">
                      <img src={url.startsWith('/') ? `${BASE}${url}` : url} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeGalleryImage(i)}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} disabled={uploading}
                className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:border-0 file:bg-primary file:text-white file:text-xs file:cursor-pointer" />
              <p className="text-xs text-gray-400 mt-1">Extra photos shown in the gallery on the product page. Select multiple files at once.</p>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving || !form.name || !form.slug} className="btn-primary text-sm disabled:opacity-60 flex-1">
                {saving ? 'Saving...' : 'Save Product'}
              </button>
              <button onClick={() => setModal(null)} className="btn-outline text-sm flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
